import React, { useEffect, useRef, useState } from "react";
import { Search, Shield, Trophy, Play, Pause, RotateCcw, MapPin, CheckCircle2, Coins } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Search the fleet",
    desc: "Curated vehicles from verified hosts. Filter by city, type, dates, and price. AI-powered concierge helps you narrow it down.",
    icon: <Search size={16} />,
    duration: 4500,
  },
  {
    n: "02",
    title: "Book with confidence",
    desc: "Secure checkout, KYC verification, escrow-backed funds, instant confirmation, and an auto-attached per-trip insurance policy.",
    icon: <Shield size={16} />,
    duration: 5200,
  },
  {
    n: "03",
    title: "Drive. Earn. Repeat.",
    desc: "Lock/unlock from the app, monitor fuel & location via telematics, and earn ZCT loyalty on every trip — redeem for future discounts.",
    icon: <Trophy size={16} />,
    duration: 4800,
  },
];

// ============ Step demos (CSS/JSX-driven, no external video) ============

function Demo01({ active }) {
  // typewriter "Los Angeles" + filter chip pop + 3 result cards stagger in
  return (
    <div className="hiw-stage" data-testid="hiw-demo-01">
      <div className="hiw-search-shell">
        <div className="hiw-search-input">
          <MapPin size={14} className="text-[var(--zc-text-3)]" />
          <span className={"hiw-typer " + (active ? "go" : "")}>Los Angeles</span>
          <span className="hiw-caret" />
        </div>
        <div className="hiw-chips">
          {["Sports", "Luxury", "Electric"].map((c, i) => (
            <span key={c} className={"hiw-chip " + (active ? `pop d${i}` : "")}>{c}</span>
          ))}
        </div>
      </div>
      <div className="hiw-results">
        {[0, 1, 2].map(i => (
          <div key={i} className={"hiw-result-card " + (active ? `slide d${i}` : "")}>
            <div className="hiw-result-img" style={{ background: ["linear-gradient(135deg,#dc2626,#991b1b)","linear-gradient(135deg,#1e40af,#172554)","linear-gradient(135deg,#0a0a0a,#404040)"][i] }}>
              <span className="hiw-result-shine" />
            </div>
            <div className="hiw-result-meta">
              <div className="hiw-result-line w70" />
              <div className="hiw-result-line w40" />
              <div className="hiw-result-price">${[389, 329, 459][i]}<span>/day</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Demo02({ active }) {
  // checkout sheet slides up, KYC modal flickers in, then green check
  return (
    <div className="hiw-stage" data-testid="hiw-demo-02">
      <div className={"hiw-checkout " + (active ? "go" : "")}>
        <div className="hiw-checkout-row">
          <div className="hiw-line w50" />
          <div className="hiw-line w20 right" />
        </div>
        <div className="hiw-checkout-row">
          <div className="hiw-pill"><Shield size={11} /> KYC required</div>
        </div>
        <div className={"hiw-kyc " + (active ? "open" : "")}>
          <div className="hiw-kyc-row">
            <div className="hiw-kyc-tile c1">ID</div>
            <div className="hiw-kyc-tile c2">ID</div>
            <div className="hiw-kyc-tile c3">SELFIE</div>
          </div>
          <div className="hiw-kyc-bar"><span /></div>
        </div>
        <div className={"hiw-confirm " + (active ? "show" : "")}>
          <CheckCircle2 size={28} />
          <div>
            <div className="hiw-confirm-title">Booked</div>
            <div className="hiw-confirm-sub">Policy auto-issued · Z-Cover</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Demo03({ active }) {
  // dashboard with telematics gauges + ZCT counter ticks up
  return (
    <div className="hiw-stage" data-testid="hiw-demo-03">
      <div className="hiw-dash">
        <div className="hiw-gauges">
          {[
            { label: "Fuel", value: 78, suffix: "%" },
            { label: "Battery", value: 94, suffix: "%" },
            { label: "Speed", value: 32, suffix: "mph" },
          ].map((g, i) => (
            <div key={g.label} className={"hiw-gauge " + (active ? `tick d${i}` : "")}>
              <div className="hiw-gauge-label">{g.label}</div>
              <div className="hiw-gauge-value">
                <span className="hiw-gauge-num">{g.value}</span><span className="hiw-gauge-suffix">{g.suffix}</span>
              </div>
              <div className="hiw-gauge-bar"><span style={{ width: `${g.value}%` }} /></div>
            </div>
          ))}
        </div>
        <div className={"hiw-zct " + (active ? "go" : "")}>
          <div className="hiw-zct-icon"><Coins size={14} /></div>
          <div>
            <div className="hiw-zct-label">ZCT earned this trip</div>
            <div className="hiw-zct-num">+<span className="hiw-zct-anim">1,895</span></div>
          </div>
        </div>
        <div className={"hiw-toast " + (active ? "show" : "")}>
          <CheckCircle2 size={14} /> Vehicle locked
        </div>
      </div>
    </div>
  );
}

// ============ Main exported component ============

export default function HowItWorksDemo() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);  // 0..1 within current step
  const tickRef = useRef(null);

  // Auto-advance through steps when playing
  useEffect(() => {
    if (!playing) return;
    const start = performance.now();
    const total = STEPS[active].duration;
    const loop = (t) => {
      const p = Math.min(1, (t - start) / total);
      setProgress(p);
      if (p < 1) {
        tickRef.current = requestAnimationFrame(loop);
      } else {
        setActive((i) => (i + 1) % STEPS.length);
      }
    };
    tickRef.current = requestAnimationFrame(loop);
    return () => tickRef.current && cancelAnimationFrame(tickRef.current);
  }, [active, playing]);

  const jumpTo = (i) => {
    setActive(i);
    setProgress(0);
  };

  const restart = () => { setActive(0); setProgress(0); setPlaying(true); };

  const D = [Demo01, Demo02, Demo03][active];

  return (
    <section className="border-t border-[var(--zc-border)]" data-testid="how-it-works-section" id="how-it-works">
      <style>{HIW_STYLES}</style>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="text-center mb-10">
          <div className="eyebrow mb-2">Process · Interactive demo</div>
          <h2 className="zc-headline text-4xl md:text-5xl">How it works</h2>
          <p className="text-[var(--zc-text-2)] text-sm md:text-base mt-3 max-w-xl mx-auto">
            Watch the three-step flow play through, or click any step to jump to it.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr,1.1fr] gap-8 items-stretch">
          {/* Left: step nav */}
          <div className="space-y-3" data-testid="hiw-steps">
            {STEPS.map((s, i) => {
              const isActive = i === active;
              return (
                <button
                  key={s.n}
                  onClick={() => jumpTo(i)}
                  className={"w-full text-left zc-card p-5 transition relative overflow-hidden " +
                    (isActive ? "border-[var(--zc-accent)] shadow-md" : "hover:border-[var(--zc-text-3)]")}
                  data-testid={`hiw-step-${s.n}`}
                  style={{ borderWidth: isActive ? 2 : 1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className={"w-10 h-10 grid place-items-center rounded-lg shrink-0 " +
                      (isActive ? "bg-[var(--zc-accent)] text-white" : "bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]")}>
                      {s.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="zc-title text-xl">{s.title}</div>
                        <div className="font-mono zc-hint">{s.n}</div>
                      </div>
                      <div className="text-[var(--zc-text-2)] text-sm leading-snug">{s.desc}</div>
                    </div>
                  </div>
                  {isActive && (
                    <div className="absolute left-0 right-0 bottom-0 h-[3px] bg-[var(--zc-bg-3)]">
                      <div className="h-full bg-[var(--zc-accent)] transition-[width] duration-100" style={{ width: `${progress * 100}%` }} />
                    </div>
                  )}
                </button>
              );
            })}

            {/* Controls */}
            <div className="flex items-center justify-between pt-2" data-testid="hiw-controls">
              <div className="text-xs text-[var(--zc-text-3)] font-mono">
                {String(active + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPlaying((p) => !p)} className="zc-btn-ghost px-4 h-9 text-sm flex items-center gap-1.5" data-testid="hiw-playpause">
                  {playing ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Play</>}
                </button>
                <button onClick={restart} className="zc-btn-ghost px-4 h-9 text-sm flex items-center gap-1.5" data-testid="hiw-restart">
                  <RotateCcw size={12} /> Restart
                </button>
              </div>
            </div>
          </div>

          {/* Right: animated demo stage */}
          <div className="zc-card p-0 overflow-hidden relative" data-testid="hiw-stage-wrap">
            <div className="hiw-screen-bezel">
              <div className="hiw-screen-dot r" />
              <div className="hiw-screen-dot y" />
              <div className="hiw-screen-dot g" />
              <div className="hiw-screen-title">zcars.app · {STEPS[active].title.toLowerCase()}</div>
            </div>
            <div className="hiw-screen-body">
              {/* re-mount on step change so animations replay */}
              <D key={active} active={progress > 0.02} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============ Inline CSS (scoped via classnames so it doesn't leak) ============
const HIW_STYLES = `
.hiw-screen-bezel { display:flex; align-items:center; gap:6px; padding:10px 14px; background:var(--zc-bg-2); border-bottom:1px solid var(--zc-border); }
.hiw-screen-dot { width:10px; height:10px; border-radius:50%; }
.hiw-screen-dot.r { background:#ef4444; }
.hiw-screen-dot.y { background:var(--zc-text-3); }
.hiw-screen-dot.g { background:#22c55e; }
.hiw-screen-title { font-family: ui-monospace, monospace; font-size:11px; color:var(--zc-text-3); margin-left:10px; }
.hiw-screen-body { padding: 28px; min-height: 460px; background:
  radial-gradient(1200px 400px at 20% -10%, var(--zc-accent-soft) 0%, transparent 50%),
  radial-gradient(900px 400px at 110% 110%, rgba(37,99,235,.10) 0%, transparent 50%),
  #fff;
  position:relative;
}

.hiw-stage { position:relative; width:100%; height:100%; min-height:404px; }

/* ---- Demo 01 ---- */
.hiw-search-shell { background:#fff; border:1px solid var(--zc-border); border-radius:14px; padding:14px; box-shadow:0 8px 24px rgba(10,10,10,.04); }
.hiw-search-input { display:flex; align-items:center; gap:8px; padding:12px 14px; background:var(--zc-bg-3); border-radius:10px; height:44px; font-size:14px; color:var(--zc-text); }
.hiw-typer { display:inline-block; max-width:0; overflow:hidden; white-space:nowrap; vertical-align:bottom; transition: max-width 1.4s ease-out; font-weight:600; }
.hiw-typer.go { max-width:200px; }
.hiw-caret { display:inline-block; width:1.5px; height:14px; background:var(--zc-accent); margin-left:2px; animation:hiwBlink 0.9s steps(1) infinite; }
@keyframes hiwBlink { 50% { opacity:0; } }
.hiw-chips { display:flex; gap:6px; margin-top:10px; }
.hiw-chip { padding:6px 12px; border-radius:999px; background:var(--zc-bg-3); color:var(--zc-text-3); font-size:11px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; opacity:0; transform:translateY(6px); }
.hiw-chip.pop { animation:hiwPop .42s .1s both; background:var(--zc-accent-soft); color:var(--zc-accent); }
.hiw-chip.pop.d1 { animation-delay:.45s; }
.hiw-chip.pop.d2 { animation-delay:.8s; }
@keyframes hiwPop { 0% {opacity:0; transform:translateY(8px) scale(.92);} 60% {transform:translateY(-2px) scale(1.04);} 100%{opacity:1; transform:translateY(0) scale(1);} }
.hiw-results { display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; margin-top:18px; }
.hiw-result-card { background:#fff; border:1px solid var(--zc-border); border-radius:12px; overflow:hidden; opacity:0; transform:translateY(14px); }
.hiw-result-card.slide { animation:hiwSlide .55s 1.4s both; }
.hiw-result-card.slide.d1 { animation-delay:1.65s; }
.hiw-result-card.slide.d2 { animation-delay:1.9s; }
@keyframes hiwSlide { 0%{opacity:0; transform:translateY(14px);} 100%{opacity:1; transform:translateY(0);} }
.hiw-result-img { aspect-ratio: 4/2.5; position:relative; overflow:hidden; }
.hiw-result-shine { position:absolute; top:0; bottom:0; left:-50%; width:30%; background:linear-gradient(120deg, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%); animation:hiwShine 2.4s 1.7s infinite; }
@keyframes hiwShine { 0%{ left:-50%; } 100%{ left:130%; } }
.hiw-result-meta { padding:10px 12px; }
.hiw-result-line { height:7px; border-radius:4px; background:var(--zc-bg-3); margin-bottom:6px; }
.hiw-result-line.w70 { width:70%; } .hiw-result-line.w40 { width:40%; }
.hiw-result-price { font-family: var(--zc-font-display); font-size:18px; color:var(--zc-text); font-weight:600; margin-top:4px; }
.hiw-result-price span { font-size:11px; color:var(--zc-text-3); margin-left:2px; }

/* ---- Demo 02 ---- */
.hiw-checkout { background:#fff; border:1px solid var(--zc-border); border-radius:14px; padding:18px; box-shadow:0 12px 30px rgba(10,10,10,.05); transform:translateY(28px); opacity:0; }
.hiw-checkout.go { animation:hiwSheet .55s .1s both; }
@keyframes hiwSheet { to { transform:translateY(0); opacity:1; } }
.hiw-checkout-row { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:10px; }
.hiw-line { height:8px; border-radius:5px; background:var(--zc-bg-3); }
.hiw-line.w50 { width:50%; } .hiw-line.w20 { width:20%; }
.hiw-line.right { margin-left:auto; }
.hiw-pill { display:inline-flex; align-items:center; gap:5px; padding:6px 11px; border-radius:999px; background:var(--zc-accent-soft); color:var(--zc-accent); font-size:11px; font-weight:700; letter-spacing:.5px; text-transform:uppercase; }
.hiw-kyc { margin-top:14px; max-height:0; overflow:hidden; opacity:0; transition:max-height .5s ease, opacity .5s ease; }
.hiw-kyc.open { max-height:240px; opacity:1; transition-delay:.6s; }
.hiw-kyc-row { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; }
.hiw-kyc-tile { aspect-ratio:1.5/1; border:1.5px dashed var(--zc-border-2); border-radius:10px; display:grid; place-items:center; font-size:10px; font-weight:800; color:var(--zc-text-3); letter-spacing:1.4px; transition: border-color .3s, background .3s, color .3s; }
.hiw-kyc.open .hiw-kyc-tile.c1 { animation:hiwTileOk .4s 1.1s both; }
.hiw-kyc.open .hiw-kyc-tile.c2 { animation:hiwTileOk .4s 1.4s both; }
.hiw-kyc.open .hiw-kyc-tile.c3 { animation:hiwTileOk .4s 1.7s both; }
@keyframes hiwTileOk { to { border-style:solid; border-color:var(--zc-success); background:rgba(22,163,74,.07); color:var(--zc-success); } }
.hiw-kyc-bar { margin-top:14px; height:4px; background:var(--zc-bg-3); border-radius:2px; overflow:hidden; }
.hiw-kyc.open .hiw-kyc-bar > span { display:block; height:100%; background:var(--zc-accent); width:0; animation:hiwBar 1.2s 2s forwards; }
@keyframes hiwBar { to { width:100%; } }
.hiw-confirm { display:flex; align-items:center; gap:12px; margin-top:18px; padding:14px; border:1px solid rgba(22,163,74,.4); background:rgba(22,163,74,.07); border-radius:12px; opacity:0; transform:translateY(8px); color:var(--zc-success); }
.hiw-confirm.show { animation:hiwIn .45s 3.3s both; }
@keyframes hiwIn { to { opacity:1; transform:translateY(0); } }
.hiw-confirm-title { font-weight:800; font-size:14px; }
.hiw-confirm-sub { font-size:12px; color:var(--zc-text-2); }

/* ---- Demo 03 ---- */
.hiw-dash { background:#fff; border:1px solid var(--zc-border); border-radius:14px; padding:18px; box-shadow:0 8px 24px rgba(10,10,10,.05); position:relative; }
.hiw-gauges { display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; }
.hiw-gauge { padding:14px; border-radius:10px; background:var(--zc-bg-2); border:1px solid var(--zc-border); opacity:0; transform:translateY(8px); }
.hiw-gauge.tick { animation:hiwIn .4s both; }
.hiw-gauge.tick.d0 { animation-delay:.1s; } .hiw-gauge.tick.d1 { animation-delay:.3s; } .hiw-gauge.tick.d2 { animation-delay:.5s; }
.hiw-gauge-label { font-size:10px; font-weight:800; color:var(--zc-text-3); text-transform:uppercase; letter-spacing:1.2px; }
.hiw-gauge-value { font-family: var(--zc-font-display); font-size:24px; font-weight:600; margin-top:6px; }
.hiw-gauge-suffix { font-size:11px; color:var(--zc-text-3); margin-left:3px; }
.hiw-gauge-bar { margin-top:8px; height:4px; border-radius:2px; background:var(--zc-bg-3); overflow:hidden; }
.hiw-gauge-bar > span { display:block; height:100%; background:var(--zc-accent); transition:width .9s .6s ease-out; }
.hiw-zct { display:flex; align-items:center; gap:14px; margin-top:18px; padding:14px 16px; border-radius:12px; background:linear-gradient(135deg, var(--zc-accent-soft) 0%, #fff 100%); border:1px solid var(--zc-accent-soft); opacity:0; transform:translateY(8px); }
.hiw-zct.go { animation:hiwIn .45s .9s both; }
.hiw-zct-icon { width:34px; height:34px; border-radius:8px; background:var(--zc-accent); color:#fff; display:grid; place-items:center; }
.hiw-zct-label { font-size:11px; font-weight:800; letter-spacing:1.2px; color:var(--zc-text-3); text-transform:uppercase; }
.hiw-zct-num { font-family: var(--zc-font-display); font-size:26px; font-weight:600; color:var(--zc-text); }
.hiw-zct-anim { display:inline-block; }
.hiw-zct.go .hiw-zct-anim { animation:hiwCount 1.4s 1s both; }
@keyframes hiwCount { 0%{opacity:0; transform:translateY(8px);} 100%{opacity:1; transform:translateY(0);} }
.hiw-toast { position:absolute; right:14px; top:14px; display:inline-flex; align-items:center; gap:6px; padding:8px 12px; border-radius:999px; background:#0a0a0a; color:#fff; font-size:11px; font-weight:700; letter-spacing:.5px; opacity:0; transform:translateY(-8px); }
.hiw-toast.show { animation:hiwToast .45s 2.3s both, hiwToastOut .35s 4.1s both; }
@keyframes hiwToast { to { opacity:1; transform:translateY(0); } }
@keyframes hiwToastOut { to { opacity:0; transform:translateY(-8px); } }

@media (max-width: 767px) {
  .hiw-results { grid-template-columns: 1fr; }
  .hiw-gauges { grid-template-columns: 1fr; }
  .hiw-kyc-row { grid-template-columns: 1fr; }
  .hiw-screen-body { min-height: auto; padding: 12px; }
  .hiw-stage { min-height: auto; }
}
`;
